import type { AssistantDoctorLink } from '../types/assistant'
import type { Case } from '../types/case'
import { USER_ROLES } from '../types/auth'

export function isReviewerOnlyCase(
  caseItem: Pick<Case, 'is_reviewer_only'>,
): boolean {
  return Boolean(caseItem.is_reviewer_only)
}

/** Assigned reviewer who is not the case owner/uploader — review-inbox style view. */
export function isAssignedReviewerView(
  caseItem: Pick<
    Case,
    'is_reviewer_only' | 'is_assigned_reviewer' | 'belongs_to_doctor_id' | 'uploaded_by_user_id'
  > | null,
  userId?: string,
): boolean {
  if (!caseItem) {
    return false
  }

  if (isReviewerOnlyCase(caseItem)) {
    return true
  }

  if (!caseItem.is_assigned_reviewer || !userId) {
    return false
  }

  return (
    caseItem.belongs_to_doctor_id !== userId && caseItem.uploaded_by_user_id !== userId
  )
}

export function assistantLinkForDoctor(
  links: AssistantDoctorLink[],
  doctorId: string | null | undefined,
): AssistantDoctorLink | undefined {
  if (!doctorId) {
    return undefined
  }

  return links.find(
    (link) => link.doctor_id === doctorId && !link.deleted_at,
  )
}

export function canManageReviewOnCase(
  role: string | null | undefined,
  caseItem: Pick<Case, 'belongs_to_doctor_id' | 'uploaded_by_user_id' | 'is_reviewer_only'>,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (isReviewerOnlyCase(caseItem)) {
    return false
  }

  if (!role) {
    return false
  }

  if (role === USER_ROLES.SUPER_ADMIN) {
    return true
  }

  if (role === USER_ROLES.DOCTOR && caseItem.belongs_to_doctor_id === userId) {
    return true
  }

  if (role === USER_ROLES.ASSISTANT) {
    if (caseItem.uploaded_by_user_id !== userId) {
      return false
    }

    const link = assistantLinkForDoctor(linkedDoctors, caseItem.belongs_to_doctor_id)

    return Boolean(link?.can_manage_review)
  }

  return false
}

export function canMutateCase(
  role: string | null | undefined,
  caseItem: Pick<Case, 'belongs_to_doctor_id' | 'uploaded_by_user_id' | 'is_reviewer_only'> &
    Partial<Pick<Case, 'open_for_assistant_review' | 'is_archived'>>,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (isReviewerOnlyCase(caseItem)) {
    return false
  }

  if (!role || !userId) {
    return false
  }

  if (role === USER_ROLES.SUPER_ADMIN) {
    return true
  }

  if (role === USER_ROLES.DOCTOR) {
    return caseItem.belongs_to_doctor_id === userId
  }

  if (role === USER_ROLES.ASSISTANT) {
    if (caseItem.uploaded_by_user_id === userId) {
      const link = assistantLinkForDoctor(linkedDoctors, caseItem.belongs_to_doctor_id)

      // Create/mutate of own uploads: linked doctor is enough (not can_create_cases).
      return Boolean(link)
    }

    if (caseItem.open_for_assistant_review && !caseItem.is_archived) {
      return Boolean(assistantLinkForDoctor(linkedDoctors, caseItem.belongs_to_doctor_id))
    }

    return false
  }

  return false
}

export function canDeleteCase(
  role: string | null | undefined,
  caseItem: Pick<Case, 'belongs_to_doctor_id' | 'uploaded_by_user_id' | 'is_reviewer_only'> &
    Partial<Pick<Case, 'open_for_assistant_review' | 'is_archived'>>,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (!canMutateCase(role, caseItem, linkedDoctors, userId)) {
    return false
  }

  // Assistants can edit doctor-opened review cases, but cannot delete them
  // unless they uploaded the case themselves.
  if (
    role === USER_ROLES.ASSISTANT &&
    userId &&
    caseItem.uploaded_by_user_id !== userId &&
    caseItem.open_for_assistant_review
  ) {
    return false
  }

  return true
}

export function canSetGlobalOnCase(
  role: string | null | undefined,
  caseItem: Pick<Case, 'belongs_to_doctor_id' | 'uploaded_by_user_id' | 'is_reviewer_only'>,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (isReviewerOnlyCase(caseItem)) {
    return false
  }

  if (!canMutateCase(role, caseItem, linkedDoctors, userId)) {
    return false
  }

  if (role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.DOCTOR) {
    return true
  }

  if (role === USER_ROLES.ASSISTANT) {
    const link = assistantLinkForDoctor(linkedDoctors, caseItem.belongs_to_doctor_id)

    return Boolean(link?.can_set_global)
  }

  return false
}

export function canUpdateOutcomeOnCase(
  role: string | null | undefined,
  caseItem: Pick<
    Case,
    'belongs_to_doctor_id' | 'uploaded_by_user_id' | 'is_archived' | 'status' | 'is_reviewer_only'
  >,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (isReviewerOnlyCase(caseItem)) {
    return false
  }

  if (!role || !userId) {
    return false
  }

  if (caseItem.is_archived || caseItem.status === 'draft') {
    return false
  }

  if (role === USER_ROLES.SUPER_ADMIN) {
    return true
  }

  if (role === USER_ROLES.DOCTOR) {
    return caseItem.belongs_to_doctor_id === userId
  }

  if (role === USER_ROLES.ASSISTANT) {
    const link = assistantLinkForDoctor(linkedDoctors, caseItem.belongs_to_doctor_id)

    return Boolean(link?.can_update_outcome)
  }

  return false
}

export function canShowCaseReviewersTab(
  can: (permission: string) => boolean,
  role: string | null | undefined,
  caseItem: Case | null,
  linkedDoctors: AssistantDoctorLink[],
  userId?: string,
): boolean {
  if (!caseItem) {
    return false
  }

  // Assigned reviewers (Cases for Review) need the Feedback tab.
  if (isReviewerOnlyCase(caseItem) || caseItem.is_assigned_reviewer) {
    return true
  }

  if (!can('cases-reviewers-list')) {
    return false
  }

  return canManageReviewOnCase(role, caseItem, linkedDoctors, userId)
}
